import axios from 'axios';
import { Product, Brand, ApiResponse, ProductSearchResult, ProductSearchOptions } from '../types';
import { environmentConfig } from '../config/environment';

const PRODUCTS_API_URL = process.env.REACT_APP_PRODUCTS_API || environmentConfig.api.productsApi;
const BRANDS_API_URL = process.env.REACT_APP_BRANDS_API || environmentConfig.api.brandsApi;

const productsApi = axios.create({
  baseURL: PRODUCTS_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const brandsApi = axios.create({
  baseURL: BRANDS_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Default api (for backward compatibility)
const api = productsApi;

export const productApi = {
  // Get all products with filters
  searchProducts: async (options: ProductSearchOptions = {}): Promise<ProductSearchResult> => {
    const params = new URLSearchParams();
    
    Object.entries(options).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });

    const response = await productsApi.get<ApiResponse<ProductSearchResult>>(`/products?${params}`);
    return response.data.data;
  },

  // Get product by ID
  getProductById: async (id: string): Promise<Product> => {
    const response = await productsApi.get<ApiResponse<{ product: Product }>>(`/products/${id}`);
    return response.data.data.product;
  },

  // Get products by SKUs
  getProductsBySkus: async (skus: string[], options: {
    brandId?: string;
    includeBrand?: boolean;
    includeInactive?: boolean;
  } = {}): Promise<Product[]> => {
    const response = await productsApi.post<ApiResponse<{ products: Product[] }>>('/products/bulk', {
      skus,
      ...options
    });
    return response.data.data.products;
  },

  // Search products by query
  searchProductsByQuery: async (query: string, brandId?: string, limit = 20): Promise<Product[]> => {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString()
    });
    
    if (brandId) {
      params.append('brandId', brandId);
    }

    const response = await productsApi.get<ApiResponse<{ products: Product[] }>>(`/products/search?${params}`);
    return response.data.data.products;
  },

  // Get product categories
  getCategories: async (brandId?: string): Promise<{ name: string; count: number }[]> => {
    const params = brandId ? `?brandId=${brandId}` : '';
    const response = await productsApi.get<ApiResponse<{ categories: { name: string; count: number }[] }>>(`/products/categories${params}`);
    return response.data.data.categories;
  }
};

export const brandApi = {
  // Get all brands
  getAllBrands: async (isActive = true): Promise<Brand[]> => {
    const response = await brandsApi.get<ApiResponse<{ brands: Brand[] }>>(`/brands?isActive=${isActive}`);
    return response.data.data.brands;
  },

  // Get brand by ID
  getBrandById: async (id: string): Promise<Brand> => {
    const response = await brandsApi.get<ApiResponse<{ brand: Brand }>>(`/brands/${id}`);
    return response.data.data.brand;
  }
};

export default api;
